import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { activityTime } from '../itemDates';
import { S } from '../strings';
import type { Category, ParsedItem } from '../types';
import { UpdateCard } from './UpdateCard';

export type CategoryGroup = {
  cat: Category;
  label: string;
  color: string;
  items: ParsedItem[];
};

type SortMode = 'newest' | 'upcoming';

const MONTHS_FI = [
  'Tammikuu',
  'Helmikuu',
  'Maaliskuu',
  'Huhtikuu',
  'Toukokuu',
  'Kesäkuu',
  'Heinäkuu',
  'Elokuu',
  'Syyskuu',
  'Lokakuu',
  'Marraskuu',
  'Joulukuu',
];

export function CategoryKanban({
  categoryGroups,
  sortMode = 'newest',
  onSelectCategory,
  isBookmarked,
  onToggleBookmark,
  isMuted,
  onToggleMute,
}: {
  categoryGroups: CategoryGroup[];
  sortMode?: SortMode;
  onSelectCategory: (cat: Category) => void;
  isBookmarked?: (itemId: string) => boolean;
  onToggleBookmark?: (item: ParsedItem) => void;
  isMuted?: (itemId: string) => boolean;
  onToggleMute?: (item: ParsedItem) => void;
}) {
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const mainScrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const syncing = useRef(false);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentWidth(contentRef.current.scrollWidth);
    }
    const ro = new ResizeObserver(() => {
      if (contentRef.current) setContentWidth(contentRef.current.scrollWidth);
    });
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [categoryGroups]);

  const handleTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (syncing.current) return;
    syncing.current = true;
    if (mainScrollRef.current) mainScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };
  const handleMainScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (syncing.current) return;
    syncing.current = true;
    if (topScrollRef.current) topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  return (
    <div>
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        className="top-scrollbar"
        style={{ marginBottom: 14 }}
      >
        <div style={{ width: contentWidth, height: 1 }} />
      </div>

      <div
        ref={mainScrollRef}
        onScroll={handleMainScroll}
        className="category-columns"
        style={{
          display: 'flex',
          gap: 18,
          overflowX: 'auto',
          alignItems: 'flex-start',
          paddingBottom: 24,
        }}
      >
        <div ref={contentRef} style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
          {categoryGroups.map((group) => {
            const visibleItems = group.items.slice(0, 8);
            const now = Date.now();
            return (
              <div key={group.cat} style={{ minWidth: 280, maxWidth: 340, flex: '1 0 280px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 14,
                    padding: '0 4px',
                  }}
                >
                  <div
                    style={{ width: 14, height: 14, borderRadius: 4, background: group.color }}
                  />
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                    {group.label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
                    {group.items.length}
                  </span>
                </div>
                <div>
                  {renderItemsWithMonthSubheaders({
                    items: visibleItems,
                    sortMode,
                    now,
                    isBookmarked,
                    onToggleBookmark,
                    isMuted,
                    onToggleMute,
                  })}
                  {group.items.length > 8 && (
                    <button
                      onClick={() => onSelectCategory(group.cat)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: 8,
                        border: '1px dashed var(--border)',
                        background: 'transparent',
                        color: group.color,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        marginTop: 4,
                      }}
                    >
                      {S.showAll(group.items.length)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {categoryGroups.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: 60,
                color: 'var(--text-muted)',
                width: '100%',
              }}
            >
              {S.emptyPanel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Walk the column's items and insert a month subheader whenever the
// grouping date (matched to the active sort) crosses a YYYY-MM boundary.
// Keeps the kanban scannable even after the read window widened and each
// category now carries 15-25 items instead of the old 5-8.
function renderItemsWithMonthSubheaders({
  items,
  sortMode,
  now,
  isBookmarked,
  onToggleBookmark,
  isMuted,
  onToggleMute,
}: {
  items: ParsedItem[];
  sortMode: SortMode;
  now: number;
  isBookmarked?: (itemId: string) => boolean;
  onToggleBookmark?: (item: ParsedItem) => void;
  isMuted?: (itemId: string) => boolean;
  onToggleMute?: (item: ParsedItem) => void;
}) {
  let lastMonthKey = '';
  const nodes: React.ReactNode[] = [];
  items.forEach((item, i) => {
    const d = groupingDate(item, sortMode, now);
    const key = monthKey(d);
    if (key !== lastMonthKey) {
      const isFuture = d.getTime() > now;
      nodes.push(<MonthHeader key={`h-${key}-${i}`} date={d} isFuture={isFuture} />);
      lastMonthKey = key;
    }
    nodes.push(
      <UpdateCard
        key={item.id}
        item={item}
        style={{ animationDelay: `${i * 0.05}s` }}
        bookmarked={isBookmarked ? isBookmarked(item.id) : false}
        onToggleBookmark={onToggleBookmark}
        muted={isMuted ? isMuted(item.id) : false}
        onToggleMute={onToggleMute}
      />,
    );
  });
  return <Fragment>{nodes}</Fragment>;
}

// Which date buckets this item into a month header. Match the sort comparator
// so the header sequence matches the visual item order — in 'upcoming' mode
// future items are bubbled up, so they get grouped by their effectiveDate
// month (e.g. "Syyskuu 2026"), while past items fall back to their activity
// date (max of publishedDate and lastChanged, the key of compareActivityDesc).
function groupingDate(item: ParsedItem, sortMode: SortMode, now: number): Date {
  if (sortMode === 'upcoming' && item.effectiveDate) {
    const t = new Date(item.effectiveDate).getTime();
    if (!isNaN(t) && t > now) return new Date(t);
  }
  return new Date(activityTime(item, now));
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function MonthHeader({ date, isFuture }: { date: Date; isFuture: boolean }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: isFuture ? 'var(--accent, #0078D4)' : 'var(--text-muted, #6B7280)',
        padding: '10px 4px 6px',
        borderBottom: '1px dashed var(--border-soft, rgba(148,163,184,0.25))',
        marginBottom: 8,
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
      }}
    >
      <span>{MONTHS_FI[date.getMonth()]} {date.getFullYear()}</span>
      {isFuture && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: 'var(--accent, #0078D4)',
            background: 'var(--accent-soft, rgba(0,120,212,0.12))',
            padding: '1px 6px',
            borderRadius: 8,
            letterSpacing: 0.4,
          }}
        >
          TULOSSA
        </span>
      )}
    </div>
  );
}
